import React, { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { adminAuth } from "@/lib/api";
import { Plus, Pencil, Trash2, Eye, EyeOff, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/university/alumni`;
const auth = () => ({ Authorization: `Bearer ${adminAuth.getToken()}` });
const blank = {
  name: "", position: "", credentials: "", biography: "",
  headshot_url: "", appointment_date: "", status: "active", public_visible: true,
};

export default function AdminUniversityBoard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/board/list`, { headers: auth() });
      setItems(r.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login?next=/admin/university/board");
      else toast.error("Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const seed = async () => {
    try {
      const r = await axios.post(`${API}/admin/seed-defaults`, {}, { headers: auth() });
      toast.success(r.data?.skipped ? "Already seeded" : `Seeded ${r.data?.created || 0}`);
      load();
    } catch (e) { toast.error("Seed failed"); }
  };
  const startNew = () => { setForm({ ...blank }); setEditing("new"); };
  const startEdit = (b) => { setForm({ ...blank, ...b }); setEditing(b.id); };
  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/board`, form, { headers: auth() });
      } else {
        await axios.patch(`${API}/admin/board/${editing}`, form, { headers: auth() });
      }
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!confirm("Delete this board member?")) return;
    try { await axios.delete(`${API}/admin/board/${id}`, { headers: auth() }); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <AdminShell door="university" title="Board of Directors">
      <div data-testid="admin-board-page">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-slate-600">{items.length} members · public at <code className="text-xs">/university/board</code></div>
          <div className="flex items-center gap-2">
            {items.length === 0 && <button onClick={seed} className="rounded-xl border h-9 px-3 text-sm" data-testid="board-seed">Seed defaults</button>}
            <button onClick={startNew} className="rounded-xl bg-purple-900 text-white h-9 px-3 text-sm inline-flex items-center gap-1.5" data-testid="board-add">
              <Plus className="w-4 h-4" />Add Board Member
            </button>
          </div>
        </div>

        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((b) => (
              <div key={b.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`board-card-${b.id}`}>
                <div className="flex items-start gap-3">
                  {b.headshot_url ? <img src={b.headshot_url} alt={b.name} className="w-14 h-14 rounded-full object-cover" /> :
                    <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center text-purple-900 text-sm">{b.name?.[0] || "?"}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base truncate">{b.name}</div>
                    <div className="text-xs text-purple-900">{b.position}</div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">{b.status}</div>
                  </div>
                </div>
                <div className="text-xs text-slate-600 mt-3">{b.credentials}</div>
                <div className="text-xs text-slate-700 mt-2 line-clamp-3">{b.biography}</div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                    {b.public_visible !== false ? <><Eye className="w-3 h-3 text-emerald-600" />Public</> : <><EyeOff className="w-3 h-3 text-slate-400" />Hidden</>}
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => startEdit(b)} className="text-purple-900" data-testid={`board-edit-${b.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del(b.id)} className="text-rose-600" data-testid={`board-del-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
            <div className="bg-white rounded-2xl p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="board-form">
              <div className="flex items-center justify-between mb-4">
                <div className="font-display text-xl">{editing === "new" ? "Add Board Member" : "Edit Board Member"}</div>
                <button onClick={() => setEditing(null)}><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <label className="col-span-2"><span className="text-xs text-slate-600">Name *</span><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" data-testid="board-name" /></label>
                <label><span className="text-xs text-slate-600">Position</span><input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
                <label><span className="text-xs text-slate-600">Appointment Date</span><input type="date" value={form.appointment_date} onChange={(e) => setForm({ ...form, appointment_date: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
                <label className="col-span-2"><span className="text-xs text-slate-600">Credentials</span><input value={form.credentials} onChange={(e) => setForm({ ...form, credentials: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
                <label className="col-span-2"><span className="text-xs text-slate-600">Headshot URL</span><input value={form.headshot_url} onChange={(e) => setForm({ ...form, headshot_url: e.target.value })} placeholder="https://..." className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
                <label className="col-span-2"><span className="text-xs text-slate-600">Biography</span><textarea value={form.biography} onChange={(e) => setForm({ ...form, biography: e.target.value })} rows={4} className="w-full border rounded-lg p-2 mt-0.5" /></label>
                <label><span className="text-xs text-slate-600">Status</span>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5">
                    {["active", "advisory", "emeritus"].map(s => <option key={s}>{s}</option>)}
                  </select></label>
                <label className="flex items-end gap-2 pb-1.5"><input type="checkbox" checked={form.public_visible !== false} onChange={(e) => setForm({ ...form, public_visible: e.target.checked })} />Display publicly</label>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setEditing(null)} className="rounded-xl border h-9 px-3 text-sm">Cancel</button>
                <button onClick={save} disabled={saving || !form.name} className="rounded-xl bg-purple-900 text-white h-9 px-4 text-sm disabled:opacity-50" data-testid="board-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
